import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import Button from 'tio-ui/components/button';
import Modal from 'tio-ui/components/modal';
import Chip from 'tio-ui/components/chip';
import { VStack } from 'tio-ui/components/layout';
import { PhWarning } from 'ember-phosphor-icons';
import { trackedFunction } from 'reactiveweb/function';
import { updateReportRequest } from '../requests/report';
import { SkipCache } from '@warp-drive/core-types/request';
import type { Store as StoreService } from '@warp-drive/core';
import type { ReportsExtension as Report } from 'tio-data/data/resources/reports.ext';
import type Owner from '@ember/owner';

interface PublishReportDialogSignature {
  Args: {
    isOpen: boolean;
    onClose: () => void;
    onPublish: (report: Report) => void;
    report: Report;
    currentCompanyId?: string | null;
    companyName?: string;
  };
}

/**
 * Publish-destination dialog (tio-front-end#9773, EPIC #9772): one surface
 * for BOTH publish channels, per-channel publish and unpublish included.
 *
 * - Partner channel — exactly the legacy publish flip: `metadata.scope`
 *   'admin_only' ⟷ 'partner_admin' (+ publishedAt / publishedCompanyId),
 *   with visibilityLevel/partnerAccessConfig set on enable. Private
 *   reports can't be *enabled* (unchecking stays allowed so an anomalous
 *   private+published report can be repaired).
 * - Internal channel — the `internal-published-at` column, deliberately
 *   orthogonal to visibilityLevel (the copy below carries the disclosure).
 *   The FE authors the timestamp; null clears it. An internal-only change
 *   leaves `metadata` completely untouched.
 *
 * Checkboxes initialize from the report's current state; a report that is
 * not published anywhere gets the sqlBacked defaults (SQL → internal only;
 * OpenSearch → partner, internal available). Confirm applies the diff in
 * one PATCH.
 */
export default class PublishReportDialog extends Component<PublishReportDialogSignature> {
  @service declare store: StoreService;

  @tracked isPublishing = false;
  @tracked hasAcknowledgedRisk = false;
  @tracked partnerChecked = false;
  @tracked internalChecked = false;

  #wasOpen = false;
  #lastSyncedReportId: string | null | undefined = undefined;

  constructor(owner: Owner, args: PublishReportDialogSignature['Args']) {
    super(owner, args);
    this.syncFromReport();
  }

  /**
   * Seed the destination checkboxes from the report's actual state. Callers
   * differ in lifecycle: some destroy the dialog on close, but the TAS
   * programs page keeps one instance mounted and only toggles @isOpen — so
   * without a re-sync a checkbox left ticked for report A would carry over
   * and silently publish report B. Mirrors publish-modal's sync watcher.
   */
  syncFromReport() {
    const report = this.args.report;
    if (!report) return;
    if (report.isPublished || report.isInternalPublished) {
      // Already published somewhere: reflect the current state so confirm
      // applies exactly what the author changes.
      this.partnerChecked = report.isPublished;
      this.internalChecked = report.isInternalPublished;
    } else {
      // Fresh publish: destination defaults follow the dataset kind.
      this.partnerChecked = !report.isSqlBacked && !report.isPrivate;
      this.internalChecked = report.isSqlBacked;
    }
    this.hasAcknowledgedRisk = false;
    this.#lastSyncedReportId = report.id;
  }

  /** Re-syncs on open and on report change; trackedFunction keeps the
   * mutations outside the render cycle. */
  syncWatcher = trackedFunction(this, async () => {
    const isOpen = this.args.isOpen;
    const reportId = this.args.report?.id;
    const needsSync =
      (isOpen && !this.#wasOpen) || (isOpen && reportId !== this.#lastSyncedReportId);

    this.#wasOpen = isOpen;

    if (needsSync) {
      this.syncFromReport();
    }
  });

  get report() {
    return this.args.report;
  }

  get companyId() {
    return this.args.currentCompanyId || this.report?.metadata?.publishedCompanyId || null;
  }

  get visibilityLevel() {
    return this.companyId ? 'partner' : 'global';
  }

  get partnerAccessConfig() {
    return this.companyId ? [Number(this.companyId)] : [];
  }

  /** Private reports can be unchecked (repair) but never checked. */
  get partnerToggleDisabled() {
    return this.report.isPrivate && !this.partnerChecked;
  }

  get enablingPartner() {
    return this.partnerChecked && !this.report.isPublished;
  }

  get disablingPartner() {
    return !this.partnerChecked && this.report.isPublished;
  }

  get enablingInternal() {
    return this.internalChecked && !this.report.isInternalPublished;
  }

  get disablingInternal() {
    return !this.internalChecked && this.report.isInternalPublished;
  }

  get partnerChanged() {
    return this.enablingPartner || this.disablingPartner;
  }

  get internalChanged() {
    return this.enablingInternal || this.disablingInternal;
  }

  get hasChanges() {
    return this.partnerChanged || this.internalChanged;
  }

  get enablingAny() {
    return this.enablingPartner || this.enablingInternal;
  }

  get disablingAny() {
    return this.disablingPartner || this.disablingInternal;
  }

  get actionLabel() {
    if (this.enablingAny && this.disablingAny) return 'Apply Changes';
    if (this.disablingAny) return 'Unpublish';
    return 'Publish';
  }

  get runningLabel() {
    if (this.actionLabel === 'Unpublish') return 'Unpublishing...';
    if (this.actionLabel === 'Apply Changes') return 'Applying...';
    return 'Publishing...';
  }

  get confirmDisabled() {
    return !this.hasAcknowledgedRisk || !this.hasChanges || this.isPublishing;
  }

  handleConfirm = async () => {
    const report = this.report;
    if (!report?.id || !this.hasAcknowledgedRisk || !this.hasChanges) return;
    // Final safety boundary, mirroring the legacy dialog: private reports
    // are never partner-published, regardless of caller.
    if (this.enablingPartner && report.isPrivate) {
      console.error('Private reports are not publishable to partners:', report.id);
      this.args.onClose();
      return;
    }

    this.isPublishing = true;
    try {
      const attrs: Parameters<typeof updateReportRequest>[1] = {};
      const partnerPatch = this.partnerChanged ? this.#buildPartnerPatch() : null;

      if (partnerPatch) {
        attrs.metadata = partnerPatch.metadata;
        attrs.visibilityLevel = partnerPatch.visibilityLevel;
        attrs.partnerAccessConfig = partnerPatch.partnerAccessConfig;
      }

      let internalPublishedAt: string | null | undefined;
      if (this.internalChanged) {
        internalPublishedAt = this.internalChecked ? new Date().toISOString() : null;
        attrs.internalPublishedAt = internalPublishedAt;
      }

      const request = updateReportRequest(report.id, attrs);
      request.cacheOptions = { [SkipCache]: true };
      await this.store.request(request);
      // Write back only known camelCase schema fields (the PATCH response
      // echoes what we sent). Assigning the raw response attributes hits the
      // SchemaRecord set trap with dasherized keys ('report-type', …), which
      // throws before anything lands and left rows stale until reload.
      if (partnerPatch) {
        report.visibilityLevel = partnerPatch.visibilityLevel;
        report.metadata = partnerPatch.metadata;
        // Mirrors the PATCH: an omitted config left the stored value alone,
        // so writing undefined back would blank a field the server still holds.
        if (partnerPatch.partnerAccessConfig !== undefined) {
          report.partnerAccessConfig = partnerPatch.partnerAccessConfig;
        }
      }
      if (this.internalChanged) {
        report.internalPublishedAt = internalPublishedAt ?? null;
      }
      this.args.onPublish(report);
      this.args.onClose();
      this.hasAcknowledgedRisk = false;
    } catch (error) {
      console.error('Failed to update publish destinations:', error);
    } finally {
      this.isPublishing = false;
    }
  };

  /** The partner-channel half of the payload, fully resolved so both the
   * PATCH body and the post-response write-back read the same values. */
  #buildPartnerPatch() {
    const report = this.report;
    const metadata = { ...(report.metadata || {}) };

    if (this.enablingPartner) {
      metadata.scope = 'partner_admin';
      metadata.publishedAt = new Date().toISOString();
      metadata.publishedCompanyId = this.companyId || undefined;
      return {
        metadata,
        visibilityLevel: this.visibilityLevel,
        partnerAccessConfig: this.partnerAccessConfig,
      };
    }

    metadata.scope = 'admin_only';
    delete metadata.publishedAt;
    delete metadata.publishedCompanyId;
    // partnerAccessConfig is omitted: unpublishing changes the channel, not
    // who the config grants, and resending it would rewrite the stored shape.
    return {
      metadata,
      visibilityLevel: report.visibilityLevel || 'private',
      partnerAccessConfig: undefined,
    };
  }

  handleCancel = () => {
    this.hasAcknowledgedRisk = false;
    this.args.onClose();
  };

  handleAcknowledgementChange = (event: Event) => {
    this.hasAcknowledgedRisk = (event.target as HTMLInputElement).checked;
  };

  handlePartnerChange = (event: Event) => {
    this.partnerChecked = (event.target as HTMLInputElement).checked;
  };

  handleInternalChange = (event: Event) => {
    this.internalChecked = (event.target as HTMLInputElement).checked;
  };

  <template>
    {{! Consumes the watcher so it actually evaluates (trackedFunction is lazy). }}
    {{this.syncWatcher.value}}
    <Modal @isOpen={{@isOpen}} @onClose={{this.handleCancel}} @size="md" data-test-publish-dialog>
      <:header>
        <div class="tio-row tio-fg-warning-body">
          <span class="tio-row-center h-9 w-9 rounded-full tio-bg-warning-soft tio-fg-warning">
            <PhWarning @size={{20}} />
          </span>
          <span class="tio-text-base-semibold tio-fg-primary">Publish Destinations</span>
        </div>
      </:header>

      <:body>
        <VStack class="tio-col-loose">
          <div class="space-y-2 text-sm tio-fg-secondary">
            <p>
              Choose where
              <strong class="tio-fg-primary">{{@report.name}}</strong>
              is published. Changes take effect immediately.
            </p>
            {{#if @report.isSqlBacked}}
              <p class="tio-inline-row-tight">
                <Chip @size="sm" @intent="brand" @appearance="faded" data-test-publish-sql-chip>
                  SQL-backed
                </Chip>
                <span class="text-xs tio-fg-muted">
                  SQL-backed reports are for internal analysis and default to internal-only.
                </span>
              </p>
            {{/if}}
          </div>

          <div
            class="rounded-md border tio-border-default tio-bg-surface divide-y tio-divide-default"
          >
            <label
              class="tio-row-top-tight p-3 text-sm {{if this.partnerToggleDisabled 'opacity-60'}}"
            >
              <input
                type="checkbox"
                checked={{this.partnerChecked}}
                disabled={{this.partnerToggleDisabled}}
                class="mt-0.5 h-4 w-4 rounded tio-border-default"
                data-test-publish-partner-checkbox
                {{on "change" this.handlePartnerChange}}
              />
              <span class="tio-col-extra-tight">
                <span class="font-medium tio-fg-primary">Partner admins</span>
                <span class="text-xs tio-fg-secondary">
                  Published outside the admin workspace (Partner Admin scope).
                </span>
                {{#if this.partnerToggleDisabled}}
                  <span class="text-xs tio-fg-muted" data-test-publish-partner-private-note>
                    Private reports can't be published to partners.
                  </span>
                {{/if}}
              </span>
            </label>

            <label class="tio-row-top-tight p-3 text-sm">
              <input
                type="checkbox"
                checked={{this.internalChecked}}
                class="mt-0.5 h-4 w-4 rounded tio-border-default"
                data-test-publish-internal-checkbox
                {{on "change" this.handleInternalChange}}
              />
              <span class="tio-col-extra-tight">
                <span class="font-medium tio-fg-primary">Internal analysts</span>
                <span class="text-xs tio-fg-secondary">
                  Listed on the Internal Analysis page for internal TIO analysts.
                </span>
                {{#if this.internalChecked}}
                  <span class="text-xs tio-fg-warning-body" data-test-publish-internal-disclosure>
                    Internal publish ignores this report's visibility level — all internal analysts
                    will see it.
                  </span>
                {{/if}}
              </span>
            </label>
          </div>

          {{#if this.hasChanges}}
            <div class="rounded-md border tio-border-default tio-bg-sunken p-3 text-sm">
              <p class="font-medium tio-fg-primary">What will happen</p>
              <ul class="mt-1 list-inside list-disc tio-fg-secondary" data-test-publish-summary>
                {{#if this.enablingPartner}}
                  <li>
                    <span class="font-medium">Partner channel:</span>
                    published —
                    {{#if this.companyId}}
                      only
                      <strong class="tio-fg-primary">{{if
                          @companyName
                          @companyName
                          "the selected company"
                        }}</strong>
                      can see this report
                    {{else}}
                      all partner admins can see this report
                    {{/if}}
                  </li>
                {{/if}}
                {{#if this.disablingPartner}}
                  <li>
                    <span class="font-medium">Partner channel:</span>
                    unpublished — partner admins lose access immediately
                  </li>
                {{/if}}
                {{#if this.enablingInternal}}
                  <li>
                    <span class="font-medium">Internal channel:</span>
                    published — appears to all internal analysts immediately
                  </li>
                {{/if}}
                {{#if this.disablingInternal}}
                  <li>
                    <span class="font-medium">Internal channel:</span>
                    unpublished — removed from the Internal Analysis page
                  </li>
                {{/if}}
              </ul>
            </div>

            <div class="rounded-md border tio-border-warning tio-bg-warning-soft p-3 text-sm">
              <p class="font-semibold tio-fg-warning-h">
                {{if this.enablingAny "Publishing risk" "Unpublishing risk"}}
              </p>
              <ul class="mt-2 list-inside list-disc space-y-1 tio-fg-warning-body">
                {{#if this.enablingAny}}
                  <li>The selected audiences may immediately access this report.</li>
                  <li>Incorrect filters or columns can publish sensitive or misleading results.</li>
                {{/if}}
                {{#if this.disablingAny}}
                  <li>Audiences may immediately lose access to this report.</li>
                  <li>Dashboards, documentation, or workflows that reference it may break.</li>
                {{/if}}
              </ul>
            </div>
          {{/if}}

          <label
            class="tio-row-top-tight rounded-md border tio-border-default tio-bg-surface p-3 text-sm tio-fg-secondary"
          >
            <input
              type="checkbox"
              checked={{this.hasAcknowledgedRisk}}
              class="mt-0.5 h-4 w-4 rounded tio-border-default tio-fg-warning focus:tio-ring-warning"
              data-test-publish-acknowledge
              {{on "change" this.handleAcknowledgementChange}}
            />
            <span>
              I understand this is a sensitive action and have verified these destination changes
              are correct.
            </span>
          </label>
        </VStack>
      </:body>

      <:footer>
        <div class="tio-flex-end gap-x-3">
          <Button @appearance="outlined" {{on "click" this.handleCancel}} data-test-publish-cancel>
            Cancel
          </Button>
          <Button
            @intent="danger"
            @isRunning={{this.isPublishing}}
            @isDisabled={{this.confirmDisabled}}
            {{on "click" this.handleConfirm}}
            data-test-publish-submit
          >
            {{if this.isPublishing this.runningLabel this.actionLabel}}
          </Button>
        </div>
      </:footer>
    </Modal>
  </template>
}
